import { animate } from "motion";

/**
 * Smooth expand / collapse for WordPress Accordion blocks (core/accordion).
 *
 * How it works:
 *   - CSS overrides the accordion-item to flex-column (kills grid row
 *     interference) and zeroes vertical panel padding (replaced by
 *     ::before / ::after pseudo-elements so height:0 = true zero).
 *   - JS animates only height + opacity on the panel.
 *   - A MutationObserver on each item's `class` attribute detects the
 *     `is-open` toggle fired by WordPress Interactivity API.
 *
 * Timing (per animation-design guidelines):
 *   - Expand  250 ms  ease-out-quart  (element entering → ease-out)
 *   - Collapse 200 ms  ease-out-quart  (exit → 20 % faster than enter)
 *   - Opacity fades in with a slight delay so content appears *as* the
 *     drawer opens. On collapse, opacity dissolves first, then height.
 */

const EASE_OUT_QUART = [0.165, 0.84, 0.44, 1];
const EXPAND_DURATION = 0.25;
const COLLAPSE_DURATION = 0.2;

/* ── Public entry ─────────────────────────────────────────────────────────── */

export const initAccordionAnimation = ({ motionEnabled }) => {
  if (!motionEnabled) return;

  const items = document.querySelectorAll(".wp-block-accordion-item");
  if (!items.length) return;

  items.forEach(setupItem);
};

/* ── Per-item setup ───────────────────────────────────────────────────────── */

function setupItem(item) {
  const panel = item.querySelector(".wp-block-accordion-panel");
  if (!panel) return;

  let running = null; // array of Motion controls for the active animation

  // Apply initial visual state
  if (item.classList.contains("is-open")) {
    setExpanded(panel);
  } else {
    setCollapsed(panel);
  }

  // Watch for the is-open class toggle (fired by WP Interactivity API)
  const observer = new MutationObserver(() => {
    const opening = item.classList.contains("is-open");

    // Cancel in-flight animation — Motion leaves styles at their current
    // interpolated value, so the next animation starts seamlessly.
    if (running) {
      running.forEach((ctrl) => ctrl.cancel());
      running = null;
    }

    running = opening ? expand(panel) : collapse(panel);
  });

  observer.observe(item, { attributes: true, attributeFilter: ["class"] });
}

/* ── State helpers ────────────────────────────────────────────────────────── */

function setCollapsed(panel) {
  panel.style.height = "0";
  panel.style.overflow = "hidden";
  panel.style.opacity = "0";
}

function setExpanded(panel) {
  panel.style.height = "auto";
  panel.style.overflow = "visible";
  panel.style.opacity = "1";
}

/* ── Expand ───────────────────────────────────────────────────────────────── */

function expand(panel) {
  panel.style.overflow = "hidden";

  // Snapshot current state (handles mid-collapse interruption)
  const currentHeight = parseFloat(getComputedStyle(panel).height) || 0;
  const currentOpacity = parseFloat(getComputedStyle(panel).opacity) || 0;

  // Temporarily expand to measure the exact rendered height
  panel.style.height = "auto";
  const targetHeight = panel.getBoundingClientRect().height;

  // Snap back so the animation starts from the current value
  panel.style.height = `${currentHeight}px`;
  panel.offsetHeight; // force reflow

  const heightCtrl = animate(
    panel,
    { height: [`${currentHeight}px`, `${targetHeight}px`] },
    { duration: EXPAND_DURATION, easing: EASE_OUT_QUART }
  );

  // Opacity fades in slightly after height starts — content appears as
  // the drawer opens rather than being visible at 1 px of height.
  const opacityDelay = currentOpacity > 0 ? 0 : EXPAND_DURATION * 0.2;
  const opacityCtrl = animate(
    panel,
    { opacity: [currentOpacity, 1] },
    {
      duration: EXPAND_DURATION * 0.8,
      delay: opacityDelay,
      easing: EASE_OUT_QUART,
    }
  );

  Promise.all([heightCtrl.finished, opacityCtrl.finished]).then(() => {
    setExpanded(panel);
  });

  return [heightCtrl, opacityCtrl];
}

/* ── Collapse ─────────────────────────────────────────────────────────────── */

function collapse(panel) {
  const startHeight = panel.getBoundingClientRect().height;
  const currentOpacity = parseFloat(getComputedStyle(panel).opacity) || 1;

  panel.style.height = `${startHeight}px`;
  panel.style.overflow = "hidden";
  panel.offsetHeight; // force reflow

  // Opacity dissolves first — content fades before the drawer closes.
  const opacityCtrl = animate(
    panel,
    { opacity: [currentOpacity, 0] },
    { duration: COLLAPSE_DURATION * 0.5, easing: EASE_OUT_QUART }
  );

  const heightCtrl = animate(
    panel,
    { height: [`${startHeight}px`, "0px"] },
    { duration: COLLAPSE_DURATION, easing: EASE_OUT_QUART }
  );

  Promise.all([heightCtrl.finished, opacityCtrl.finished]).then(() => {
    setCollapsed(panel);
  });

  return [heightCtrl, opacityCtrl];
}
